/***
*
*   FILE UPLOADER
*   Drag & drop file upload component
*   Can upload multiple files
*   Includes fallback for older browser
*
*   PROPS
*   name: input name
*   max: max number of files
*   maxFileSize: limit size of file in kb§
*   accept: array of filetypes to accept, eg. ['jpg', 'gif']
*   
**********/

import { useState, useRef, useEffect } from 'react';
import { Icon, Button, ClassHelper, useTranslation } from 'components/lib';
import { Label } from '../label/label';
import { Error } from '../error/error';
import Style from './file.tailwind.js';
import { MediaGrid } from '../../global/NodeLayout/CodeEditorComponents/MediaGrid';
import { Backdrop } from '@mui/material';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';


export const ViewImage = ({ imageLink, imageFullUrl, onlyImage }) => {

  const { t } = useTranslation();
  const containerName = process.env.REACT_APP_AZURE_BLOB_CONTAINER_NAME_LOGO;
  const [openUrl, setOpenUrl] = useState()

  return (
    <>
      {openUrl &&
        <Backdrop
          sx={{
            color: '#fff', zIndex: (theme) => Math.max.apply(Math, Object.values(theme.zIndex)) + 1
          }}
          open
        >
          <ClickAwayListener onClickAway={() => { setOpenUrl() }}>
            <div style={{ backgroundColor: 'white', marginTop: "50px" }}>
              <MediaGrid
                mediaLinks={[openUrl]}
                openOnClick={false}
                isImage={onlyImage}
                allowOverflow={true}
              />
            </div>
          </ClickAwayListener >
        </Backdrop>
      }
      <p
        style={{ cursor: 'pointer' }}
        onClick={() => {
          const url = imageFullUrl
            ? imageFullUrl
            : `${process.env.REACT_APP_GLOBAL_MEDIA_URL}/${containerName}/org_logo/${imageLink}`;
          setOpenUrl(url);
        }}
      >
        {t('global.form.file.view')}
      </p>
    </>
  )
}

export function FileInput(props) {

  const { t } = useTranslation();
  const fileInputRef = useRef();
  const [dragging, setDragging] = useState(false);
  const [valid, setValid] = useState(undefined);
  const [error, setError] = useState(props.errorMessage || t('global.form.file.error.unselected'));
  const [fileStore, setFileStore] = useState([]);

  // Update the onChange values.
  useEffect(() => {
    if (fileStore.length) {
      props.onChange(props.name, fileStore, valid);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileStore]);
  const latestFilesRef = useRef([]);

  useEffect(() => {
    latestFilesRef.current = fileStore;
  }, [fileStore]);

  useEffect(() => {
    return () => {
      latestFilesRef.current.forEach(f => URL.revokeObjectURL(f.url));
    };
  }, []);




  function validate(files) {
    // check files exist
    if (files.length) {
      if ((files.length + fileStore.length) > props.max) {
        setValid(false);
        setError(`${t('global.form.file.error.max')} ${props.max}`);
        return false;
      }
      const newFileStore = [...fileStore];

      for (let i = 0; i < files.length; i++) {

        const file = files[i];
        const type = file.type.substring(file.type.indexOf('/') + 1).toString();

        if (props.onlyImage) {
          // Check if the file is an image (JPEG, PNG, or JPG)
          if (!/^image\/(jpeg|png|jpg)$/.test(file.type)) {
            setValid(false);
            setError(t('global.form.file.error.onlyImage'));
            return false;
          }
        }

        // validate file type
        if (props.accept?.length && !props.accept.includes(type)) {

          setValid(false);
          setError('.' + file.type.substring(file.type.indexOf('/') + 1) + t('global.form.file.error.fileUpload'));
          return false;
        }

        // validate file size (in kb)
        else if (props.maxFileSize && file.size > (1024 * props.maxFileSize)) {

          setValid(false);
          if (props.maxFileSize > 1000) {
            setError(t('global.form.file.error.maxMB', { maxFileSize: props.maxFileSize / 1024 }));
          } else {
            setError(t('global.form.file.error.maxKB', { maxFileSize: props.maxFileSize }));
          }
          return false;
        }

        else {
          // store the file in form store
          newFileStore.push({
            name: file.name,
            data: file,
            url: URL.createObjectURL(file),
            size: file.size,
            type: file.type

          });
          setValid(true);
        }
      }
      setFileStore(newFileStore);
    }
  }

  function deleteFile(file) {
    URL.revokeObjectURL(file.url);

    setFileStore(prev => {
      const updated = prev.filter(f => f.url !== file.url);
      const valid = updated.length > 0;
      setValid(valid);
      if (!valid) setError(`Please select file`);
      props.onChange(props.name, updated, valid); // always uses the latest array
      return updated;
    });
  }

  function onDrag(e, state) {

    e.preventDefault();
    e.stopPropagation();
    setDragging(state);

  }

  const fileStyle = ClassHelper(Style, {

    dropzone: true,
    dragging: dragging,
    error: valid === false ? true : false,
    success: props.value?.length && valid === true

  });
  return (
    <div className={Style.file}>

      {props.label &&
        <Label
          text={props.label}
          required={props.required}
          for={props.name}
        />
      }

      <div className={fileStyle}

        onDragOver={e => onDrag(e, true)}
        onDragEnter={e => onDrag(e, true)}
        onDragLeave={e => onDrag(e, false)}
        onDrop={e => {
          onDrag(e, false)
          validate(e.dataTransfer.files)
        }}>
        { /* fallback for old browsers */}
        <input
          type="file"
          multiple
          accept={props.onlyImage ? "image/png, image/jpeg, image/jpg" : "*/*"}
          className={Style.legacyInput}
          ref={fileInputRef}
          onChange={e => {
            validate(e.target.files);
          }}
        />


        {props.value?.length ? <>
          <div className={Style.label}>
            <Icon image={'check'} className={Style.labelIcon} />
            <FileList files={fileStore} delete={deleteFile} />
          </div>
        </> :
          props.imageLink || props.imageFullUrl ? <>
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', justifyContent: 'space-around', alignItems: 'center', height: "100%" }}>
              {/* <div style={{ width: '107px', height: '107px', display: 'flex', alignItems: 'center' }}>
                <img src={props.imageFullUrl ? props.imageFullUrl : `${process.env.REACT_APP_GLOBAL_MEDIA_URL}/${containerName}/org_logo/${props.imageLink}`} alt="org_logo" />
              </div> */}
              <ViewImage
                imageLink={props.imageLink}
                imageFullUrl={props.imageFullUrl}
                onlyImage={props.onlyImage}
              />
              <div style={{ cursor: 'pointer' }} className={Style.textContainer} onClick={e => fileInputRef.current.click()}>
                <Icon image={'upload'} className={Style.labelIcon} />
                <div className={Style.labelText}>{t('global.form.file.change')}</div>
              </div>
            </div>
          </> :
            <div className={Style.label} onClick={e => fileInputRef.current.click()} style={{ cursor: 'pointer' }}>
              <Icon image={'upload'} className={Style.labelIcon} />
              <div className={Style.labelText}>{t('global.form.file.dropzone')}</div>
            </div>
        }

      </div>

      {valid === false && <Error message={error} />}

    </div>
  );
}

function FileList(props) {

  if (!props.files?.length)
    return false;

  return (
    <div>
      {props.files.map(file => {

        return (
          <div key={file.name} className={Style.fileListItem}>

            <span>{file.name}</span>

            <Button
              icon='x'
              color='#d95565'
              size={13}
              className={Style.fileListButton}
              action={e => props.delete(file)}
            />
          </div>
        )
      })}
    </div>
  )
}
